<template>
  <div class="address-selector-refactored">
    <inline-descriptor
      ref="inlineDescriptorRef"
      :id="`address-refactored-${id}`"
      :model-value="computedModel"
      :schema="computedSchema"
      :separators="separators"
      :editable="true"
      :enable-menu="true"
      :move-to-body="true"
      :menu-z-index="menuZIndex"
      @update:modelValue="onUpdateModelValue"
      @update-menu="handleUpdateMenu"
      @keydown.up.exact.prevent="onArrowUp"
      @keydown.down.exact.prevent="onArrowDown"
      @keydown.enter.exact.prevent="onEnter"
      @keydown.esc.exact.prevent="closeMenu"
    >
      <template #menu-content>
        <div class="address-selector-refactored__menu">
          <aux-selector
            ref="auxSelectorRef"
            style="min-width: 320px; height: 100%;"
            :show-search-header="false"
            :limit-height-to-container="true"
            :allow-free-input="false"
            :allow-multiple="false"
            :model-type="AuxSelectorDataType.AddressHints"
            :filter-model-value="auxFilterString"
            :component-model="componentModel"
            :model-value="selectedHints"
            @update:model-value="onHintsModelUpdate"
            @selectorClosed="onSelectorClosed"
          />
        </div>
      </template>
    </inline-descriptor>

    <q-icon
      v-show="showClear && hasFilterValue"
      class="pointer hover-green q-ml-sm"
      style="align-self: baseline"
      name="arrow_back"
      size="18px"
      color="grey-7"
      title="clear"
      @click.stop.prevent="clearAddressModel"
    />
    <q-btn
      class="q-px-none q-mx-none unset-items-center"
      style="margin-top: 1px;"
      flat
      dense
      v-if="showDelete"
      :aria-label="$t('delete_filter')"
      icon="close"
      size="sm"
      tabindex="-1"
      color="grey-8"
      @click.stop.prevent="emitDelete"
    >
      <q-tooltip :delay="1000">
        {{ $t('remove') }}
      </q-tooltip>
    </q-btn>
  </div>
</template>

<script lang="ts">
import _, { debounce } from 'lodash';
import { reactive } from 'vue';
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import InlineDescriptor, { SchemaField, Token } from '@/components/multiedit/temporary/InlineDescriptor.vue';
import { auxStore } from '@/store/auxillary';
import { AddressInputComponentOut, AddressInputContainer } from '@/models/interfaces/entity-settings';
import type { AddressAutocompleteElement } from '@/models/interfaces/entity-settings';
import { CommonEntity } from '@/models/interfaces/common';
import { AuxSelectorDataType } from '@/components/common/aux-selector/types';

type AddressHintOption = {
  Id: number;
  Components: AddressAutocompleteElement[];
};

// Перенос «намерения сфокусировать следующий уровень» через перемонт
// компонента. Контекст: VariableDisplayList ключует адресные айтемы по
// Entity.Id; после выбора подсказки Entity.Id меняется и Vue полностью
// пересоздаёт AddressSelectorRefactored. Поэтому штатный $refs текущего
// инстанса до nextTick не доживает. Старый инстанс пишет сюда entityId →
// уровень, новый инстанс после initContainer забирает запись и активирует
// нужную секцию у своего свежего InlineDescriptor.
const pendingFocusByEntityId = new Map<number, number>();

@Component({
  name: 'AddressSelectorRefactored',
  components: { InlineDescriptor },
})
export default class AddressSelectorRefactored extends Vue {
  AuxSelectorDataType = AuxSelectorDataType;

  @Prop({ default: {} as CommonEntity })
  modelValue!: CommonEntity;
  @Prop({ default: false })
  useRequired!: boolean;
  @Prop({ default: false })
  larger!: boolean;
  @Prop({ default: false })
  showClear!: boolean;
  @Prop({ default: false })
  showDelete!: boolean;
  @Prop({ default: 'address-refactored' })
  id!: string;
  // Строить содержимое без искусственного setTimeout(10) — через $nextTick,
  // чтобы при mount-on-edit (например, InlineAddressEditor) селектор не мелькал пустым
  @Prop({ default: false })
  instantInit!: boolean;
  // Прокидывается в .fmenu (меню автокомплита). 0 — штатный z-index 1000.
  // Поднимается только там, где селектор внутри высокого дропдауна.
  @Prop({ default: 0 })
  menuZIndex!: number;

  @Emit('update:modelValue') emitModelValue(arg: CommonEntity) { return arg; }
  @Emit('chainSelected') emitChain(arg: any) { return arg; }
  @Emit('onDeleteClick') emitDelete(arg: any) { return arg; }

  store: any | undefined = undefined;

  // Reactive-инициализаторы по образцу ParameterInline. В vue-facing-decorator
  // class-fields раскрываются в data() через `new cons(...)` для каждого
  // инстанса, поэтому reactive([])/reactive({}) тут per-instance, а не shared.
  // В mounted() ниже ключевые структуры всё равно переприсваиваются свежим
  // reactive(...) — это и есть ParameterInline-паттерн.
  container: AddressInputComponentOut[] = reactive([]);
  computedSchema: SchemaField[] = reactive([]);
  computedModel: Record<string, Token[]> = reactive({});
  selectedHints: AddressHintOption[] = reactive([]);

  componentModel: { container: AddressInputComponentOut[] | null; activeLevel: number } = reactive({
    container: null,
    activeLevel: 0,
  });

  separators = [','];
  currentLevel = 0;
  fieldKey: string | null = null;
  sectionText = '';
  auxFilterString = '';

  // Защита от обратного цикла v-model: parent эхом возвращает theModel, мы не
  // должны на это перестраивать computedModel и ломать активную секцию.
  private guardModelSync = false;

  // У стратегии AddressHints в aux-selector закрытие меню происходит
  // автоматически после выбора (closeAfterSelect=true) — он эмитит
  // selectorClosed. Если мы при выборе сами переводим фокус на следующий
  // уровень, штатный close нужно подавить ровно на одно срабатывание, иначе
  // меню моргнёт между hideMenu и последующим openMenu из активации.
  private suppressNextSelectorClose = false;

  // Дебаунс синхронизации фильтра/componentModel — повторяет ParameterInline.
  // Инициализируется в mounted, чтобы избежать гонки с created()->initContainer.
  debouncedFilterUpdate!: any;

  created() {
    this.store = auxStore();
    this.initContainer();
  }

  mounted() {
    this.debouncedFilterUpdate = debounce(() => this.applyFilterUpdate(), 300);
  }

  get modelValueMut() {
    return _.cloneDeep(this.modelValue);
  }

  get hasFilterValue(): boolean {
    return this.container.some((el) => (el.Value || '').length > 0);
  }

  // Аналог onModelValueChange в ParameterInline: при внешнем апдейте проп-модели
  // переразложить значение по контейнеру и пересобрать computedModel целиком
  // через reactive(...) — это и есть ParameterInline-паттерн.
  @Watch('modelValueMut', { deep: true })
  modelWatcher(newv: CommonEntity, oldv: CommonEntity) {
    if (this.guardModelSync) {
      return;
    }
    if (_.isEqual(newv, oldv)) {
      return;
    }
    this.applyModelToContainer(newv);
    this.computedModel = reactive(this.createModel());
  }

  get theModel() {
    return this.modelValue;
  }

  set theModel(arg: CommonEntity) {
    if (!arg) {
      return;
    }
    this.guardModelSync = true;
    this.emitModelValue(arg);
    this.guardModelSync = false;
    this.applyModelToContainer(arg);
    this.computedModel = reactive(this.createModel());
  }

  clearAddressModel() {
    this.theModel = reactive({
      Id: 0,
      Name: '',
    });
    this.emitAddressChain();
  }

  onHintsModelUpdate(val: AddressHintOption[]) {
    this.selectedHints = val;
    if (!this.selectedHints.length || !this.container.length) {
      return;
    }

    // Фиксируем активный уровень ДО мутации container, чтобы потом
    // переходить именно на следующий после него уровень.
    const prevActiveLevel = Number(this.currentLevel);

    const selected = this.selectedHints[0];
    const byLevel = new Map<number, string>();
    selected.Components.forEach((c) => {
      byLevel.set(Number(c.Level), c.Value);
    });
    this.container.forEach((el) => {
      el.Value = byLevel.get(Number(el.Level)) || '';
    });

    // Перед эмитом modelValue ищем уровень для следующей активации и кладём
    // в кросс-инстансный stash под новым Entity.Id. Иначе после смены ключа
    // в VariableDisplayList наш текущий инстанс будет уже размонтирован, и
    // активировать секцию через $refs на нём не получится.
    const newEntityId = Number(selected.Id) || 0;
    const next = this.container.find(
      (el) => Number(el.Level) > prevActiveLevel && !el.Value
    );
    if (next && newEntityId) {
      pendingFocusByEntityId.set(newEntityId, Number(next.Level));
      // Подавляем штатный auto-close от aux-selector (closeAfterSelect=true)
      // на этом инстансе ровно один раз — иначе hideMenu проскочит до того,
      // как новый инстанс сам откроет меню в нужной секции.
      this.suppressNextSelectorClose = true;
    }

    this.computedModel = reactive(this.createModel());
    this.emitModelValue({
      Id: newEntityId,
      Name: this.composeNameFromContainer(),
    });
    this.selectedHints = reactive([]);
    // Фолбэк-путь для случая, когда родитель не пересоздаёт компонент
    // (нет смены :key). Тогда тот же инстанс активирует следующий уровень
    // штатно через свой InlineDescriptor. Если же пересоздание произошло —
    // вызов уйдёт «в пустоту» и сработает stash-путь в initContainer нового
    // инстанса.
    this.focusNextEmptyLevel(prevActiveLevel);
  }

  onArrowUp() {
    (this.$refs.auxSelectorRef as any)?.trackArrowUp(1);
  }

  onArrowDown() {
    (this.$refs.auxSelectorRef as any)?.trackArrowDown(1);
  }

  onEnter() {
    const selected = (this.$refs.auxSelectorRef as any)?.selectCurrent?.();
    if (selected) {
      this.closeMenu();
    }
  }

  // После выбора подсказки (мышью или клавиатурой — не важно) переводим
  // фокус на первый незаполненный уровень глубже prevActiveLevel. Активация
  // делается штатным публичным методом InlineDescriptor.activateSectionByKey,
  // который повторяет путь перехода стрелкой вправо: ставит caret в первый
  // токен и вызывает updateMenu(2) → handleUpdateMenu откроет меню и
  // обновит currentLevel. Если незаполненного уровня глубже нет — оставляем
  // прежнее поведение и закрываем меню.
  focusNextEmptyLevel(prevActiveLevel: number) {
    const next = this.container.find(
      (el) => Number(el.Level) > prevActiveLevel && !el.Value
    );
    if (!next) {
      this.closeMenu();
      return;
    }
    // Если флаг ещё не выставлен (родитель не пересоздаёт компонент при смене
    // Entity.Id) — подавляем штатный auto-close от aux-selector, чтобы меню
    // не моргнуло между hideMenu и openMenu из активации.
    if (!this.suppressNextSelectorClose) {
      this.suppressNextSelectorClose = true;
    }
    const that = this;
    const targetKey = `addr_level_${Number(next.Level)}`;
    // Ждём nextTick: реактивные обновления computedModel/modelValue должны
    // долететь до InlineDescriptor и его applyModel — чтобы секция была
    // перерисована до того, как мы её активируем.
    this.$nextTick(() => {
      const inlineDescriptor = that.$refs.inlineDescriptorRef as any;
      if (!inlineDescriptor || typeof inlineDescriptor.activateSectionByKey !== 'function') {
        // Инстанс уже размонтирован (родитель сменил :key). Тогда фокус
        // подхватит новый инстанс через consumePendingFocus в initContainer.
        return;
      }
      inlineDescriptor.activateSectionByKey(targetKey);
    });
  }

  // Обёртка над closeMenu для @selectorClosed: позволяет подавить ровно
  // одно закрытие, когда мы сами переводим фокус на следующий уровень после
  // выбора подсказки.
  onSelectorClosed() {
    if (this.suppressNextSelectorClose) {
      this.suppressNextSelectorClose = false;
      return;
    }
    this.closeMenu();
  }

  // Закрытие меню по образцу ParameterInline: прячем меню и обнуляем
  // transient state селектора, чтобы при следующем открытии не было
  // stale-данных.
  closeMenu() {
    const that = this;
    this.$nextTick(() => {
      const inlineDescriptor = that.$refs.inlineDescriptorRef as any;
      if (inlineDescriptor) {
        inlineDescriptor.hideMenu();
        that.componentModel.container = null;
      }
    });
  }

  // Обработчик update-menu по образцу ParameterInline:
  //  - eventKind=0 (input): только дебаунс фильтра;
  //  - eventKind!=0/1 (клик/смена секции): синхронно apply + openMenu;
  //  - eventKind=1 (blur): hideMenu, обнулить container, эмитнуть chain.
  handleUpdateMenu(value: {
    fieldKey: string | null;
    sectionText: string;
    eventKind: number;
    opts: any | undefined;
  }) {
    this.fieldKey = value.fieldKey;
    this.sectionText = value.sectionText || '';
    const level = this.extractLevel(value.fieldKey);
    if (level >= 0) {
      this.currentLevel = level;
    }

    if (this.debouncedFilterUpdate && value.eventKind === 0) {
      this.debouncedFilterUpdate();
    }

    this.$nextTick(() => {
      if (value.eventKind !== 0 && value.eventKind !== 1) {
        this.debouncedFilterUpdate?.cancel?.();
        this.applyFilterUpdate();
      }
      const inlineDescriptor = this.$refs.inlineDescriptorRef as any;
      if (!inlineDescriptor) {
        return;
      }
      if (value.eventKind === 1) {
        inlineDescriptor.hideMenu();
        this.componentModel.container = null;
        this.emitAddressChain();
      } else {
        inlineDescriptor.openMenu('section');
        // На input актуализируем активный уровень в контейнере (для payload
        // следующего загруза подсказок) и эмитим chain — фильтрация по тексту
        // должна работать сразу, как в legacy AddressSelector.
        if (value.eventKind === 0) {
          this.syncActiveLevelValue(this.sectionText);
          this.emitAddressChain();
        }
      }
    });
  }

  // Аналог onUpdateModelValue в ParameterInline: на сохранение модели из
  // дескриптора — обновляем контейнер и эмитим цепочку. Сам computedModel
  // здесь НЕ пересобираем: текущая модель уже отражает ввод пользователя,
  // дескриптор сам поддерживает каретку через structuredModelClone.
  onUpdateModelValue(newModel: Record<string, Token[]>) {
    this.updateContainerFromModel(newModel);
    this.emitAddressChain();
  }

  // ParameterInline-паттерн: при изменении подменяем componentModel целиком
  // через reactive({...}). Точечная мутация (как было раньше) ломала логику
  // обнаружения изменений в дочерних селекторах.
  applyFilterUpdate() {
    if (this.auxFilterString !== this.sectionText) {
      this.auxFilterString = this.sectionText;
    }
    const cont = this.componentModel.container;
    const cont_new = _.cloneDeep(this.container);
    if (
      this.componentModel.activeLevel !== this.currentLevel ||
      !_.isEqual(cont, cont_new)
    ) {
      this.componentModel = reactive({
        container: cont_new,
        activeLevel: this.currentLevel,
      });
    }
  }

  private extractLevel(fieldKey: string | null): number {
    if (!fieldKey) {
      return -1;
    }
    const parts = fieldKey.split('_');
    const last = parts[parts.length - 1];
    const level = Number(last);
    return Number.isFinite(level) ? level : -1;
  }

  private composeNameFromContainer(): string {
    return this.container
      .map((el) => el.Value || '')
      .filter((value) => !!value)
      .join(', ');
  }

  private syncActiveLevelValue(text: string) {
    const activeLevel = Number(this.currentLevel);
    const active = this.container.find((el) => Number(el.Level) === activeLevel);
    if (active) {
      active.Value = text;
    }
  }

  private applyNameToContainer(name: string) {
    this.container.forEach((el) => {
      el.Value = '';
    });
    const parts = name.split(',');
    const maxIndex = this.container.length - 1;
    for (let index = 0; index <= maxIndex; index++) {
      if (!this.container[index]) {
        continue;
      }
      if (index < maxIndex) {
        this.container[index].Value = (parts[index] || '').trim();
      } else {
        this.container[index].Value = parts.slice(index).join(',').trim();
      }
    }
  }

  private applyModelToContainer(model: CommonEntity) {
    if (!this.container.length) {
      return;
    }
    this.applyNameToContainer(model?.Name || '');
  }

  private tokensFromValue(value: string): Token[] {
    const rawTokens = (value.match(/(\s+|[^\s]+)/g) || []).map((tok) => ({
      text: tok,
      type: 'string',
      clickable: false,
    }));
    const fullTokens: Token[] = [];
    rawTokens.forEach((token) => {
      if (/\s+/.test(token.text)) {
        fullTokens.push({ text: ' ', type: 'space', clickable: false });
      } else {
        fullTokens.push(token);
      }
    });
    return fullTokens;
  }

  private createSchema(): SchemaField[] {
    return this.container.map((field) => ({
      key: `addr_level_${field.Level}`,
      title: '',
      type: 'select',
      // separator берётся у "следующего" поля при постановке разделителя на
      // предыдущую секцию, поэтому для стабильной запятой между предпоследней
      // и последней секциями задаём separator на всех address-полях.
      separator: this.separators[0],
      options: [{
        AddressNodeId: field.AddressNodeId,
        Level: field.Level,
      }],
      placeholder: field.Placeholder,
    }));
  }

  private createModel(): Record<string, Token[]> {
    const model: Record<string, Token[]> = {};
    this.container.forEach((field) => {
      model[`addr_level_${field.Level}`] = this.tokensFromValue(field.Value || '');
    });
    return model;
  }

  private updateContainerFromModel(newModel: Record<string, Token[]>) {
    this.container.forEach((field) => {
      const key = `addr_level_${field.Level}`;
      const tokens = newModel[key];
      field.Value = Array.isArray(tokens)
        ? tokens.map((token) => token.text).join('')
        : '';
    });
  }

  emitAddressChain() {
    let allEmpty = false;
    let requiredEmpty = false;

    const request: AddressInputContainer = {
      Components: [],
      ActiveLevel: this.currentLevel,
    };

    for (let a = 0; a < this.container.length; a++) {
      request.Components.push({
        AddressNodeId: this.container[a].AddressNodeId,
        Level: this.container[a].Level,
        Value: allEmpty ? '' : this.container[a].Value,
      });
      if (!this.container[a].Value) {
        allEmpty = a > 0 ? allEmpty && true : true;
      }
      if (this.container[a].Required && !this.container[a].Value) {
        requiredEmpty = true;
      }
    }

    this.emitChain({ request, correct: allEmpty || !requiredEmpty });
  }

  initContainer() {
    if (!this.store) {
      return;
    }
    const build = async () => {
      await this.store.getAddressContainerStructure();
      // store.getCachedAddressContainer — общий массив-синглтон в кеше стора.
      // reactive(arr) от него вернул бы один shared Proxy на все инстансы
      // (Vue 3 кеширует Proxy по target). Делаем deep клон, дальше оборачиваем
      // свежим reactive(...) — у каждого инстанса собственный реактивный массив.
      this.container = reactive(
        _.cloneDeep((this.store.getCachedAddressContainer || []) as AddressInputComponentOut[])
      );
      this.applyModelToContainer(this.theModel);
      this.computedSchema = reactive(this.createSchema());
      this.computedModel = reactive(this.createModel());
      // Сразу подкладываем актуальный componentModel, чтобы первый клик в
      // секцию не дёргал изменение container в aux-selector в момент открытия.
      this.componentModel = reactive({
        container: _.cloneDeep(this.container),
        activeLevel: this.currentLevel,
      });
      this.emitAddressChain();
      this.consumePendingFocus();
    };

    if (this.instantInit) {
      this.$nextTick(build);
    } else {
      setTimeout(build, 10);
    }
  }

  // Если предыдущий инстанс перед перемонтом запомнил уровень для активации
  // под нашим Entity.Id — забираем запись и активируем соответствующую
  // секцию через публичное API InlineDescriptor. Запись консьюмится сразу,
  // чтобы не сработала повторно при последующих внутренних обновлениях.
  private consumePendingFocus() {
    const myId = Number(this.theModel?.Id) || 0;
    if (!myId || !pendingFocusByEntityId.has(myId)) {
      return;
    }
    const targetLevel = pendingFocusByEntityId.get(myId) as number;
    pendingFocusByEntityId.delete(myId);

    const that = this;
    this.$nextTick(() => {
      that.$nextTick(() => {
        const inlineDescriptor = that.$refs.inlineDescriptorRef as any;
        if (!inlineDescriptor || typeof inlineDescriptor.activateSectionByKey !== 'function') {
          return;
        }
        inlineDescriptor.activateSectionByKey(`addr_level_${targetLevel}`);
      });
    });
  }
}
</script>

<style scoped lang="scss">
.address-selector-refactored {
  display: inline;
  max-width: 100%;
  min-width: 0;
}

.address-selector-refactored__menu {
  min-width: 320px;
  height: 100%;
}
</style>
